import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Bell, X, MailCheck, Trash2, Loader2, BellRing, Folder } from 'lucide-react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { BACKEND_API_URL } from '../utils/api';
import { cn } from '@/lib/utils';
import { useFirebaseMessaging } from '@/hooks/useFirebaseMessaging.jsx';

// shadcn/ui components
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';

// Helper function to format time
const formatDistanceToNow = (date) => {
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'just now';
};

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const NotificationCenter = () => {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const lastNotificationIdRef = useRef(null);
    const navigate = useNavigate();

    // Firebase Cloud Messaging hook for real-time push notifications
    const {
        permissionStatus,
        requestPermission,
        isSupported: fcmSupported
    } = useFirebaseMessaging();

    // Lightweight function to fetch only unread count (for polling)
    // Note: Browser notifications are handled by FCM (Firebase Cloud Messaging)
    // This only updates the badge count to avoid duplicate notifications
    const fetchUnreadCount = useCallback(async () => {
        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/notifications/count/`, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            const newCount = response.data.unread_count;

            setUnreadCount(prev => {
                if (newCount > prev) {
                    // Update lastNotificationIdRef to track which notifications we've seen
                    axios.get(`${REACT_APP_BACKEND_API_URL}/api/notifications/`, {
                        headers: {
                            'Authorization': `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                        params: { since_id: lastNotificationIdRef.current || 0 }
                    }).then(notifResponse => {
                        const latestNotifications = notifResponse.data.notifications;
                        if (latestNotifications.length > 0) {
                            // FCM handles browser notifications - just update the tracking ref
                            lastNotificationIdRef.current = Math.max(...latestNotifications.map(n => n.id));
                        }
                    }).catch(err => {
                        console.error('Error fetching latest notification:', err);
                    });
                }

                return prev !== newCount ? newCount : prev;
            });
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    }, []);

    // Full function to fetch all notifications (only when drawer is open)
    // Note: Browser notifications are handled by FCM - this just updates the drawer list
    const fetchNotifications = useCallback(async (isPolling = false) => {
        try {
            if (!isPolling) {
                setLoading(true);
            }
            const accessToken = Cookies.get('access_token');

            const params = {};
            if (isPolling && lastNotificationIdRef.current) {
                params.since_id = lastNotificationIdRef.current;
            }

            const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/notifications/`, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                params: params
            });
            const newNotifications = response.data.notifications;
            const newCount = response.data.unread_count;

            if (isPolling && lastNotificationIdRef.current) {
                if (newNotifications.length > 0) {
                    setNotifications(prev => {
                        const existingIds = new Set(prev.map(n => n.id));
                        const uniqueNew = newNotifications.filter(n => !existingIds.has(n.id));
                        if (uniqueNew.length > 0) {
                            // FCM handles browser notifications - just update the drawer list
                            const merged = [...prev, ...uniqueNew].sort((a, b) =>
                                new Date(b.created_at) - new Date(a.created_at)
                            );
                            if (merged.length > 0) {
                                lastNotificationIdRef.current = Math.max(...merged.map(n => n.id));
                            }
                            return merged;
                        }
                        return prev;
                    });
                }
            } else {
                setNotifications(newNotifications);
                if (newNotifications.length > 0) {
                    lastNotificationIdRef.current = Math.max(...newNotifications.map(n => n.id));
                }
            }

            setUnreadCount(prev => prev !== newCount ? newCount : prev);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            if (!isPolling) {
                setLoading(false);
            }
        }
    }, []);

    // Fetch full notifications when drawer opens
    useEffect(() => {
        if (open) {
            fetchNotifications();
        }
    }, [open, fetchNotifications]);

    // Poll for unread count every 2 seconds
    useEffect(() => {
        fetchUnreadCount();

        const interval = setInterval(() => {
            if (open) {
                fetchNotifications(true);
            } else {
                fetchUnreadCount();
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [open, fetchUnreadCount, fetchNotifications]);

    const handleMarkAsRead = async (notificationId) => {
        try {
            const accessToken = Cookies.get('access_token');
            await axios.post(`${REACT_APP_BACKEND_API_URL}/api/notifications/${notificationId}/read/`, {}, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                },
            });
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            const accessToken = Cookies.get('access_token');
            await axios.post(`${REACT_APP_BACKEND_API_URL}/api/notifications/read-all/`, {}, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                },
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const handleClearAll = async () => {
        if (notifications.length === 0) return;

        if (!window.confirm('Are you sure you want to archive all notifications?')) {
            return;
        }

        try {
            const accessToken = Cookies.get('access_token');
            await axios.post(`${REACT_APP_BACKEND_API_URL}/api/notifications/archive-all/`, {}, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                },
            });
            setNotifications([]);
            setUnreadCount(0);
            lastNotificationIdRef.current = null;
        } catch (error) {
            console.error('Error archiving all notifications:', error);
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.read) {
            await handleMarkAsRead(notification.id);
        }

        // Handle canvas comment notifications - navigate to the item with focus mode and comment highlight
        if (notification.notification_type === 'canvas_mention' && notification.item_id) {
            const commentParam = notification.comment_id ? `&commentId=${notification.comment_id}` : '';
            navigate(`/?itemId=${notification.item_id}&focusMode=true${commentParam}`);
            setOpen(false);
            return;
        }

        // Handle channel notifications - navigate to the channel message
        if (notification.message_id && notification.channel_id) {
            navigate(`/channels/${notification.channel_id}?messageId=${notification.message_id}`);
            setOpen(false);
        }
    };

    const formatTime = (dateString) => {
        try {
            return formatDistanceToNow(new Date(dateString));
        } catch (e) {
            return '';
        }
    };

    const getNotificationText = (notification) => {
        switch (notification.notification_type) {
            case 'mention':
                return 'mentioned you';
            case 'thread_reply':
                return 'replied in thread';
            case 'personal_channel':
                return 'messaged in your channel';
            case 'canvas_mention':
                return 'mentioned you in a comment';
            default:
                return 'sent a notification';
        }
    };

    // Helper to determine if this is a canvas notification
    const isCanvasNotification = (notification) => {
        return notification.notification_type === 'canvas_mention';
    };

    // Get the location text (channel name or item name)
    const getLocationText = (notification) => {
        if (isCanvasNotification(notification)) {
            return notification.item_name || 'Canvas Item';
        }
        return notification.channel_name;
    };

    // Get the preview text (message or comment)
    const getPreviewText = (notification) => {
        if (isCanvasNotification(notification)) {
            return notification.comment_text;
        }
        return notification.message?.message;
    };

    return (
        <>
            {/* Trigger Button */}
            <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(true)}
                className="relative"
                data-name="notification-center-button"
            >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-semibold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </Button>

            {/* Notification Sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-md p-0 flex flex-col"
                    hideCloseButton
                >
                    {/* Header */}
                    <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
                        <div className="flex items-center justify-between">
                            <SheetTitle className="text-lg font-semibold">
                                Notifications
                                {unreadCount > 0 && (
                                    <Badge variant="secondary" className="ml-2 text-xs">
                                        {unreadCount} new
                                    </Badge>
                                )}
                            </SheetTitle>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => setOpen(false)}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Push Notification Permission Banner */}
                        {fcmSupported && permissionStatus === 'default' && (
                            <div className="mt-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
                                <div className="flex items-start gap-3">
                                    <BellRing className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground">
                                            Enable push notifications
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Get instant notifications when someone mentions you
                                        </p>
                                        <Button
                                            size="sm"
                                            onClick={requestPermission}
                                            className="mt-2 h-7 text-xs"
                                        >
                                            Enable notifications
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        {(unreadCount > 0 || notifications.length > 0) && (
                            <div className="flex gap-2 mt-2">
                                {unreadCount > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleMarkAllAsRead}
                                        className="text-xs"
                                    >
                                        <MailCheck className="h-3.5 w-3.5 mr-1.5" />
                                        Mark all read
                                    </Button>
                                )}
                                {notifications.length > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleClearAll}
                                        className="text-xs text-destructive hover:text-destructive"
                                    >
                                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                        Clear all
                                    </Button>
                                )}
                            </div>
                        )}
                    </SheetHeader>

                    {/* Content */}
                    <ScrollArea className="flex-1">
                        {loading ? (
                            /* Loading State */
                            <div className="p-4 space-y-4">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex gap-3">
                                        <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                                        <div className="flex-1 space-y-2">
                                            <Skeleton className="h-4 w-3/4" />
                                            <Skeleton className="h-3 w-1/2" />
                                            <Skeleton className="h-3 w-full" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : notifications.length === 0 ? (
                            /* Empty State */
                            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                <div className="rounded-full bg-muted p-4 mb-4">
                                    <Bell className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <p className="text-muted-foreground font-medium">No notifications</p>
                                <p className="text-sm text-muted-foreground/70 mt-1">
                                    You're all caught up!
                                </p>
                            </div>
                        ) : (
                            /* Notification List */
                            <div className="divide-y divide-border">
                                {notifications.map((notification) => (
                                    <button
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={cn(
                                            "w-full text-left px-4 py-3 transition-colors hover:bg-accent/50 focus:outline-none focus:bg-accent/50",
                                            !notification.read && "bg-primary/5"
                                        )}
                                    >
                                        <div className="flex gap-3">
                                            {/* Avatar */}
                                            <Avatar className="h-10 w-10 shrink-0">
                                                <AvatarImage src={notification.author_picture} />
                                                <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                                                    {notification.author_username?.[0]?.toUpperCase()}
                                                </AvatarFallback>
                                            </Avatar>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start gap-2">
                                                    <p className="text-sm flex-1">
                                                        <span className="font-semibold text-foreground">
                                                            {notification.author_username}
                                                        </span>
                                                        <span className="text-muted-foreground ml-1">
                                                            {getNotificationText(notification)}
                                                        </span>
                                                    </p>
                                                    {!notification.read && (
                                                        <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
                                                    )}
                                                </div>

                                                {/* Location (Channel name or Item name) */}
                                                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-0.5">
                                                    {isCanvasNotification(notification) ? (
                                                        <Folder className="h-3 w-3" />
                                                    ) : '#'}
                                                    {getLocationText(notification)}
                                                </p>

                                                {/* Message/Comment preview - backend already truncates to 150 chars */}
                                                {getPreviewText(notification) && (
                                                    <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
                                                        {getPreviewText(notification)}
                                                    </p>
                                                )}

                                                {/* Timestamp */}
                                                <p className="text-xs text-muted-foreground/70 mt-1.5">
                                                    {formatTime(notification.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </SheetContent>
            </Sheet>
        </>
    );
};

export default NotificationCenter;
